import { captureException } from 'lib/reporting';

/**
 * This client is used for variable persistance across loads.
 * Data is not guaranteed to persist if user has cookies off.
 */
class StorageClient {
  public static init() {
    try {
      sessionStorage.setItem('testtest', 'testtest');
      sessionStorage.getItem('testtest');
      sessionStorage.removeItem('testtest');

      localStorage.setItem('testtest', 'testtest');
      localStorage.getItem('testtest');
      localStorage.removeItem('testtest');

      this.supportsStorage = true;
    } catch {
      this.supportsStorage = false;
    }
  }

  public static setSessionItem(key: SessionStorageKey, value: string) {
    this.sessionData[key] = value;
    if (this.supportsStorage) {
      try {
        sessionStorage.setItem(key, value);
      } catch (e) {
        // The storage can run out of space and hit QuotaExceeded
        captureException(e, 'warning', { keys: Object.keys(sessionStorage) });
        this.supportsStorage = false;
      }
    }
  }

  public static getSessionItem(key: SessionStorageKey) {
    if (this.supportsStorage) {
      this.sessionData[key] = sessionStorage.getItem(key);
    } else if (!(key in this.sessionData)) {
      this.sessionData[key] = null;
    }
    return this.sessionData[key];
  }

  public static removeSessionItem(key: SessionStorageKey) {
    delete this.sessionData[key];
    if (this.supportsStorage) {
      sessionStorage.removeItem(key);
    }
  }

  public static setLocalItem(key: LocalStorageKey, value: string) {
    this.localData[key] = value;
    if (this.supportsStorage) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        // The storage can run out of space and hit QuotaExceeded
        captureException(e, 'warning', { keys: Object.keys(localStorage) });
        this.supportsStorage = false;
      }
    }
  }

  public static getLocalItem(key: LocalStorageKey) {
    if (this.supportsStorage) {
      this.localData[key] = localStorage.getItem(key);
    } else if (!(key in this.localData)) {
      this.localData[key] = null;
    }
    return this.localData[key];
  }

  public static removeLocalItem(key: LocalStorageKey) {
    delete this.localData[key];
    if (this.supportsStorage) {
      localStorage.removeItem(key);
    }
  }

  private static sessionData: { [key: string]: string | null } = {};
  private static localData: { [key: string]: string | null } = {};
  private static supportsStorage: boolean;
}

StorageClient.init();
export default StorageClient;

// These constants include keys from all apps despite this being a shared util.
// This is because separate apps are accessible on the same domain, and it's
// plausible for users of one app to use another (e.g. dashboard users testing
// verify flow) and we don't want key collisions.
export enum LocalStorageKey {
  // Shared
  IpStackCacheKey = 'cached-ipstack-location',
  LocaleInfoCacheKey = 'cached-locale-info',

  // Dashboard
  DashboardCurrentEnvironmentId = 'dashboard:current-environment-id',
  DashboardRefreshTokenSession = 'PERSONA-DASHBOARD-REFRESH',
  DashboardCasesNextPageSettingDefault = 'dashboard:cases:next-page-setting-default',
  DashboardLayoutEditorCopiedModule = 'dashboard:copied-module',
  DashboardGraphQueryParameters = 'dashboard:graph:query-parameters',
  DashboardInquirySimulateBannerDismissed = 'dashboard:inquiry:simulate-banner-dismissed',
  DashboardITEInternalEditingEnabled = 'dashboard:ite:internal-editing-enabled',
  DashboardITEPreviewSimulatePanelOpen = 'dashboard:ite:simulate-panel-open',
  DashboardITEScreenEditorSelectedScreenMode = 'dashboard:ite-screen-editor-selected-screen-mode',
  DashboardITEScreenEditorSelectedComponentPanelSettings = 'dashboard:ite-screen-editor-selected-component-panel-settings',
  DashboardITEPreviewStepsPanelOpen = 'dashboard:ite:simulate-steps-panel-open',
  DasbhoardITEScreenEditorCopiedComponent = 'dashboard:ite:screen-editor:copied-component',
  DashboardITEScreenDimensions = 'dashboard:ite:screen-dimensions',
  DashboardReportsTableVisibleColumns = 'dashboard:reports:visible-columns',
  DashboardWorkflowRunsTableSelectedColumns = 'dashboard:workflow-runs:selected-columns',
  DashboardWorkflowEditorCollapsedSteps = 'dashboard:workflow-editor:collapsed-steps',
  DashboardWorkflowEditorCopiedSteps = 'dashboard:workflow-editor:copied-steps',
  DashboardWorkflowEditorRunCodeFormInputs = 'dashboard:workflow-editor:run-code-form-inputs',
  DashboardActivityAt = 'dashboard:activity-at',
  GroupProperties = 'ajs_group_properties',
  UserTraits = 'ajs_user_traits',
  APIListInfoBanner = 'api-list-info-banner',
  APIEditInfoBanner = 'api-edit-info-banner',
  WebhooksInfoBanner = 'webhooks-info-banner',
  EventsInfoBanner = 'events-info-banner',
  DashboardImporterStepperFormInputs = 'dashboard:importer-stepper-form-inputs',
  DashboardEventBus = 'dashboard:event-bus',

  // These are set in webflow.
  // utm_source may actually be from a `ref` param. They're squashed for convenience.
  MarketingGclid = 'marketing:gclid',
  MarketingUtmSource = 'marketing:utm-source',
  MarketingUtmMedium = 'marketing:utm-medium',
  MarketingUtmCampaign = 'marketing:utm-campaign',
  MarketingUtmTerm = 'marketing:utm-term',
  MarketingUtmContent = 'marketing:utm-content',
  MarketingUtmAudience = 'marketing:utm-audience',
  MarketingUtmOther = 'marketing:utm-other',

  // Inquiry
  InquirySandboxBannerHidden = 'inquiry:sandbox-banner-hidden',
}

export enum SessionStorageKey {
  // Dashboard
  CommentModuleDrafts = 'dashboard:comment-module-drafts',
  SarModuleTabIdx = 'dashboard:sar-module-tab-idx',
  SectionedCardIdx = 'dashboard:sectioned-card-idx',
  CaseTemplateSettingTabIdx = 'dashboard:case-template-setting-tab-idx',

  // Inquiry
  InquiryParams = 'inquiry:params-key',
  InquiryAccessToken = 'inquiry:access-token',
  /**
   * Device ID sent in response headers. Stored in session storage instead of local storage for cookie policy purposes.
   * This data is best-effort; we primarily rely on cookies for tracking device ID.
   */
  InquiryDeviceId = 'inquiry:device-id',
}
